<script setup>
  import { defineProps, defineEmits, onMounted, onUnmounted } from 'vue'
  import { ref, inject } from 'vue'
  import { useAuthStore } from '@/stores/auth.store'
  import { fetchWrapper } from '@/helpers/fetch-wrapper'
  import { marked } from 'marked'
  import { useI18n } from 'vue-i18n'
  import { CustomPopover } from '@/components/CustomPopover.ts'
  const authStore = ref(false)
  authStore.value = useAuthStore()
  const { t } = useI18n()
  const grammarHint = ref(false)
  const copied = ref(false)

  const props = defineProps({
    message: Object,
    popoverVisible: Boolean
  })
  
  const emit = defineEmits(['deleteMessage', 'hidePopovers'])
  const audiomanager = inject('audiomanager')
  const popovermanager = inject('popovermanager')

  const message = props.message


  onMounted(() => {
    popovermanager.addPopover(message)
  })

  onUnmounted(() => {
    popovermanager.removePopover(message)
  })

  function getVisibleText(element) {
    const clone = element.cloneNode(true)
    clone.querySelectorAll('rt, rp').forEach(el => el.remove())
    return clone.textContent.trim()
  }

  function handleAnnotationClick(event) {
    console.log("handleAnnotationClick", event)
    const clickable = event.target.closest('.clickable')
    if (clickable) {
      event.stopPropagation()
      console.log("clickable", clickable)
      const text = getVisibleText(clickable)
      console.log("text", text)
      // play the audio for the clicked text
      const { user } = useAuthStore()
      const lang = clickable.dataset.lang
      if (!lang || !text) return
      const url = `/api/tts/?lang=${lang}&text=${encodeURIComponent(text)}&token=${user.token}`
      audiomanager.setPlaybackSpeed(1.0)
      audiomanager.play(url)
    }
  }


  function play_audio_slow(message) {
    clearTooltips()
    audiomanager.setPlaybackSpeed(0.5)
    audiomanager.play(message.audio).then(() => {
      message.playing_slow = true
      audiomanager.setOnEnded(() => {
        message.playing = false
        message.playing_slow = false
      })
    })
  }
  function play_audio(message) {
    clearTooltips()
    audiomanager.setPlaybackSpeed(1.0)
    audiomanager.play(message.audio).then(() => {
      message.playing = true
      audiomanager.setOnEnded(() => {
        message.playing = false
        message.playing_slow = false
      })
    })
  }
  function stop_audio(message) {
    audiomanager.pause()
    message.playing = false
    message.playing_slow = false
    clearTooltips()
  }

  function delete_message(message) {
    console.log('delete_message', message)
    emit('deleteMessage', message)
  }
  function show_grammar(grammar) {
    grammarHint.value = grammar
  }
  function hide_grammar() {
    grammarHint.value = false
  }
  async function show_translation(message) {
    if (!message.translation) {
      const response = await fetchWrapper.post(`/translate/`, {messageID: message.messageID})
      message.translation = response
    }
    message.show = 'translation'
    clearTooltips()
  }
  function show_text(message) {
    message.show = 'text'
    clearTooltips()
  }
  function hidePopovers() {
    // iterate all div with a role of popover and set their display to none
    const popovers = document.querySelectorAll('[role="tooltip"]')
    popovers.forEach((popover) => {
      popover.style.display = 'none'
    })
  }
  async function show_vocab(message) {
    if (message.vocab.length == 0) {
      message.vocab = await fetchWrapper.post(`/vocab/message/`, { messageID: message.messageID})
    }
    message.show_vocab = !message.show_vocab
    clearTooltips()
  }

  function showSelectableText(message) {
    navigator.clipboard.writeText(message.text)
    clearTooltips()
    copied.value = true
  }

  function clearTooltips () {
    const tooltipsInner = document.querySelector('.tooltip-inner')
    if (tooltipsInner !== null) {
      tooltipsInner.remove()
    }
    const tooltips = document.querySelector('.tooltips')
    if (tooltips !== null) {
      tooltips.remove()
    }
    const tooltipsArrow = document.querySelector('.tooltip-arrow')
    if (tooltipsArrow !== null) {
      tooltipsArrow.remove()
    }
    hidePopovers()
  }

  /* called by the popper to offset the popover window
  placement is a a string like 'bottom', 'top', 'left', 'right'
  reference is a dict of { x, y, width, height } of the thing we clicked on
  popper is the {x, y, width, height } of the popover window
  we want to orient it relative to the p.msg element
  */
  // eslint-disable-next-line no-unused-vars
  function offset ({ placement, reference, popper })  {
    if (placement == 'bottom') {
      const el = message_outer.value
      if (!el) {
        return []
      }
      const message_rect = el.getBoundingClientRect()
      const bottom_offset = message_rect.bottom - (reference.y + reference.height)
      // the default value is [0, 8] to make room for the arrow, so we add our offset to that
      return [0, bottom_offset + 8]

    } else {
      return []
    }
  }
  const message_outer = ref(null)
  function onShowPopover() {
    message.popoverVisible = true
    emit('hidePopovers', message)
  }
  function onHidePopover() {
    message.popoverVisible = false
  }

</script>

<template>
  <div class="row g-0" style="width: 99%">
    <div class="col-md-12">
      <div class="msg-container text-start" :class="{ 'left': message.role == 'ai', 'right': message.role == 'user' }">
        <CustomPopover placement="bottom" trigger="click" :offset="offset" @show="onShowPopover" @hide="onHidePopover">
          <template #content>
            <div class="message-controls">
              <span v-if="message.show == 'text'" @click="show_translation(message)">
                <i class="action-icon bi-translate" v-c-tooltip="t('chat.show-translation')"></i>
              </span>
              <span v-if="message.show == 'translation'" @click="show_text(message)">
                <i class="action-icon bi-translate" v-c-tooltip="t('chat.show-original-language')"></i>
              </span>
              <span v-if="message.audio && !message.playing" @click="play_audio(message, $event)">
                <i class="action-icon bi-play-circle" v-c-tooltip="t('chat.play-audio')"></i>
              </span>
              <span v-if="message.audio && message.playing" @click="stop_audio(message, $event)">
                <i class="action-icon bi-pause-circle" v-c-tooltip="t('chat.stop-audio')"></i>
              </span>
              <span v-if="message.audio && !message.playing_slow" @click="play_audio_slow(message, $event)">
                <i class="action-icon bi-hourglass" v-c-tooltip="t('chat.play-audio-slowly')"></i>
              </span>
              <span v-if="message.audio && message.playing_slow" @click="stop_audio(message, $event)">
                <i class="action-icon bi-pause-circle" v-c-tooltip="t('chat.stop-audio')"></i>
              </span>
              <span v-if="message.role == 'user'" @click="delete_message(message)">
                <i class="action-icon bi-trash" v-c-tooltip="t('chat.delete-message')"></i>
              </span>
              <span v-if="message.role == 'ai'" @click="delete_message(message)">
                <i class="action-icon bi-recycle" v-c-tooltip="t('chat.regenerate-message')"></i>
              </span>
              <span @click="show_vocab(message)">
                <i class="action-icon bi-book" v-c-tooltip="t('chat.show-vocabulary')"></i>
              </span>
              <span @click="showSelectableText(message)"><i class="action-icon bi-scissors"/></span>
            </div>
          </template>
          <template #toggler="{ id, on }">
              <span v-if="message.grammar && grammarHint">&nbsp;&nbsp;&nbsp;<i @click="hide_grammar()" class="action-icon bi-question-circle me-2 fs-5" /></span>
              <span v-else-if="message.grammar && !grammarHint">&nbsp;&nbsp;&nbsp;<i @click="show_grammar(message.grammar)" class="action-icon bi-question-circle me-2 fs-5" /></span>
              <p :class="['msg', authStore.fontClass]" :ref="(el) => { message_outer = el }" :messageID="message.messageID" :aria-described-by="id" v-on="on">
                <span @click="handleAnnotationClick" v-if="message.show == 'text' && message.annotation" v-html="marked(message.annotation)"></span>
                <span v-if="message.show == 'text' && !message.annotation" v-html="marked(message.text)"></span>

                <span v-if="message.show == 'translation'">
                  <span v-if="message.show == 'translation' && message.translation.annotation" v-html="message.translation.annotation"></span>
                  <span v-else-if="message.show == 'translation'" v-html="message.translation.text"></span>
                  <hr />
                  <span v-if="message.show == 'translation'" class="translation-text" v-html="message.translation.translation"></span><br/>
                </span>
              </p>
              
              <span v-if="copied">&nbsp;&nbsp;&nbsp;<small><i class="bi-scissors" /></small></span>

          </template>
        </CustomPopover>
      </div>
      <template v-if="grammarHint">
        <div class="msg-container hint">
        <p class="msg" v-if="grammarHint">
          <span v-if="grammarHint" class="">{{ grammarHint }}</span>
        </p>
      </div>
      </template>
    </div>
  </div>
  <div v-if="message.show_vocab" class="row">
    <div class="col-12">
      <vocab-entry v-for="vocab in message.vocab" :vocab="vocab" v-bind:key="vocab.id" class="card col-11">
      </vocab-entry>
    </div>
  </div>
</template>

<style>
.clickable {
  cursor: pointer;
  color: rgb(0, 110, 255) !important;
}
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
div.message {
  display: block;
  padding: 15px;
}
.action-icon {
  font-size: 150%;
  cursor: pointer;
}

.msg-container {
  width: 100%;
  display: flex;
}
.msg-container.right {
  justify-content: flex-end;
}

.msg-container.left {
  justify-content: flex-start
}

.msg-container.hint {
  justify-content: flex-end;
}

.msg-container.right > .msg {
  background: linear-gradient(#80c8df, #3664ab);
  box-shadow: 0 2px 5px rgb(0 0 0 / 20%);
  color: white;
}

.msg-container.right > .msg >>> p  {
  background-color:transparent;
  color: white;
  margin-bottom: 0;
}

.msg-container.hint > .msg {
  background-color:#ebb434;
  color: white;
  font-size: small;
}

.msg-container.hint > .msg >>> p  {
  color: white;
  font-size: small;
}

.msg-container.left > .msg {
  background-color:#e5e9eb;
  box-shadow: 0 2px 5px rgb(0 0 0 / 20%);
  color: black;
}
.msg-container.left > .msg >>> p{
  margin-bottom: 0;
}
.msg-container.left > .msg >>> span {
  color: var(--secondary-text-color);
}

.msg-container.left .msg:before{
  display: none;
  content: "";
  position: absolute;
  z-index: 0;
  bottom: 0;
  left: -7px;
  height: 20px;
  width: 20px;
  background: #ccc;
  border-bottom-right-radius: 15px;

}

.msg-container.left .msg:after{
  display: none;
  content: "";
  position: absolute;
  z-index: 1;
  bottom: 0;
  left: -10px;
  width: 10px;
  height: 20px;
  background: white;
  border-bottom-right-radius: 10px;
}

.msg-container.right .msg:before{
  display: none;
  content: "";
  position: absolute;
  z-index: 0;
  bottom: 0;
  right: -7px;
  height: 20px;
  width: 20px;
  background: #147efb;
  border-bottom-left-radius: 15px;
}

.msg-container.right .msg:after{
  display: none;
  content: "";
  position: absolute;
  z-index: 1;
  bottom: 0;
  right: -10px;
  width: 10px;
  height: 20px;
  background: white;
  border-bottom-left-radius: 10px;
}

.msg{
  max-width: 80%;
  border-radius: 10px;
  padding: 10px;
  position: relative;
  display: inline-block;
}

.message-controls {
  display: inline-block;
  direction: ltr;
  padding: 0px;
}

i {
  padding-left: 10px;
}

.translation-text {
  font-style: italic;
  font-size: 0.85em;
  color: #6c757d;
}


</style>
<style>
ruby {
  font-family: "Microsoft YaHei";
}
</style>
